/**
 * IMPORTS
 */
import { useEffect, useMemo, useState } from "react";

import { CalendarX2, Search } from "lucide-react";

import { NotificacoesSino } from "@components/notificacoes/notificacoes-sino";
import { PaginationControls } from "@components/pagination-controls/pagination-controls";

import { useMinhaAgenda } from "@/hooks/agenda-cliente/use-minha-agenda";

import type { IFiltrosMinhaAgenda, IStatusCompromisso } from "@/interface/agenda-cliente";
import { generatePagination } from "@/utils/generate-pagination";

import { CompromissoItem } from "./components/compromisso-item";

const POR_PAGINA = 20;
const ATRASO_BUSCA_MS = 400;

type FiltroStatus = IStatusCompromisso | "todos";

const FILTROS_STATUS: { valor: FiltroStatus; label: string }[] = [
  { valor: "pendente", label: "Pendentes" },
  { valor: "concluido", label: "Concluídos" },
  { valor: "cancelado", label: "Cancelados" },
  { valor: "todos", label: "Todos" },
];

const MENSAGEM_VAZIO: Record<FiltroStatus, string> = {
  pendente: "Você não tem compromissos pendentes.",
  concluido: "Nenhum compromisso concluído.",
  cancelado: "Nenhum compromisso cancelado.",
  todos: "Sua agenda ainda não tem compromissos.",
};

/**
 * Agenda do cliente (somente leitura): compromissos que a equipe Letscom marcou como visíveis.
 * A API ordena por data (mais próximos primeiro); atrasados e de hoje ganham destaque.
 */
function AgendaClientePage() {
  const [status, setStatus] = useState<FiltroStatus>("pendente");
  const [pagina, setPagina] = useState(1);
  const [termo, setTermo] = useState("");
  const [busca, setBusca] = useState("");

  useEffect(() => {
    const temporizador = setTimeout(() => {
      setBusca(termo.trim());
      setPagina(1);
    }, ATRASO_BUSCA_MS);
    return () => clearTimeout(temporizador);
  }, [termo]);

  const filtros = useMemo<IFiltrosMinhaAgenda>(
    () => ({
      page: pagina,
      per_page: POR_PAGINA,
      status: status === "todos" ? undefined : status,
      search: busca || undefined,
    }),
    [pagina, status, busca]
  );

  const agenda = useMinhaAgenda(filtros);
  const compromissos = agenda.data?.data ?? [];
  const paginacao = agenda.data?.pagination;
  const ultimaPagina = paginacao?.last_page ?? 1;

  const trocarStatus = (novo: FiltroStatus) => {
    setStatus(novo);
    setPagina(1);
  };

  const irParaPagina = (acao: number | "prev" | "next") => {
    if (acao === "prev") setPagina((atual) => Math.max(1, atual - 1));
    else if (acao === "next") setPagina((atual) => Math.min(ultimaPagina, atual + 1));
    else setPagina(acao);
  };

  return (
    <div className="w-full">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1 p-4 pb-2">
          <h2 className="text-2xl font-medium text-black">Agenda</h2>
          <p className="text-sm text-gray-500">
            Ligações, reuniões, visitas e outros compromissos combinados com a equipe Letscom.
          </p>
        </div>
        <div className="ml-auto mr-4">
          <NotificacoesSino />
        </div>
      </div>

      <section className="mx-4 mb-4 rounded-xl border border-gray-200 bg-white shadow-sm">
        <header className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 py-3">
          <fieldset className="flex flex-wrap gap-1">
            <legend className="sr-only">Filtrar por status</legend>
            {FILTROS_STATUS.map((filtro) => (
              <button
                key={filtro.valor}
                type="button"
                onClick={() => trocarStatus(filtro.valor)}
                aria-pressed={status === filtro.valor}
                className={`rounded-md px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-custom900 ${
                  status === filtro.valor
                    ? "bg-gray-900 text-white"
                    : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                {filtro.label}
              </button>
            ))}
          </fieldset>
          <div className="relative ml-auto w-full sm:w-56">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={termo}
              onChange={(evento) => setTermo(evento.target.value)}
              placeholder="Buscar na agenda..."
              aria-label="Buscar na agenda"
              className="h-10 w-full rounded-md border border-gray-300 bg-white pl-8 pr-3 text-sm text-gray-900 caret-gray-900 outline-none placeholder:text-gray-400 focus-visible:ring-2 focus-visible:ring-gray-custom900"
            />
          </div>
        </header>

        <div className={agenda.isPlaceholderData ? "opacity-60 transition-opacity" : ""}>
          {agenda.isLoading ? (
            <div className="space-y-2 p-2">
              {[0, 1, 2, 3].map((indice) => (
                <div key={indice} className="h-20 animate-pulse rounded-md bg-gray-100" />
              ))}
            </div>
          ) : agenda.isError ? (
            <div className="p-8 text-center">
              <p className="text-sm text-gray-500">Não foi possível carregar a agenda.</p>
              <button
                type="button"
                onClick={() => agenda.refetch()}
                className="mt-4 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
              >
                Tentar novamente
              </button>
            </div>
          ) : compromissos.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 p-8 text-center text-gray-500">
              <CalendarX2 className="size-8" aria-hidden="true" />
              <p className="text-sm">
                {busca ? "Nenhum compromisso encontrado." : MENSAGEM_VAZIO[status]}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {compromissos.map((compromisso) => (
                <CompromissoItem key={compromisso.id} compromisso={compromisso} />
              ))}
            </div>
          )}
        </div>

        {paginacao && compromissos.length > 0 && (
          <footer className="border-t border-gray-200 px-4 py-3">
            <PaginationControls
              currentPage={paginacao.current_page}
              lastPage={paginacao.last_page}
              paginations={generatePagination(paginacao.current_page, paginacao.last_page)}
              totalItems={paginacao.total}
              itemsPerPage={paginacao.per_page}
              onGoToPage={irParaPagina}
            />
          </footer>
        )}
      </section>
    </div>
  );
}

/**
 * EXPORTS
 */
export { AgendaClientePage };
